// Every decision the Invoices screen makes, as pure functions: the URL
// contract, the query the loader and the screen share, the period arithmetic
// (a month-name table, never a `Date` getter — `datetime.guard` bans those),
// the pill bands, and which actions an invoice's state allows.
//
// The screens stay thin so this file can be tested without a DOM.

import type { InvoicesQuery } from '@/api/resources';
import type {
  InvoiceAction,
  InvoiceActionKind,
  InvoiceEmailAttempt,
  InvoiceEmailStatus,
  InvoiceLine,
  InvoiceState,
  InvoiceSummary,
  InvoicingSettings,
} from '@/api/types';
import { nyToday } from '@/lib/datetime';
import { searchEnum } from '@/lib/search-enum';
import type { Band } from '@/lib/system-metrics';

// ── Periods ─────────────────────────────────────────────────────────────────

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** A billing cycle is an ISO 8601 week: `2026-W37`, Monday to Sunday. */
const PERIOD_RE = /^(\d{4})-W(0[1-9]|[1-4]\d|5[0-3])$/;
const DAY_MS = 86_400_000;

export function isPeriod(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const parts = periodParts(value);
  return parts != null && parts.w <= weeksInIsoYear(parts.y);
}

function periodParts(period: string): { y: number; w: number } | null {
  const match = PERIOD_RE.exec(period);
  if (!match) return null;
  return { y: Number(match[1]), w: Number(match[2]) };
}

function periodFromParts(y: number, w: number): string {
  return `${String(y).padStart(4, '0')}-W${String(w).padStart(2, '0')}`;
}

/** Monday of ISO week `w` of ISO year `y`, as a UTC date. Jan 4 is always in week 1. */
function isoWeekMonday(y: number, w: number): Date {
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const offset = (jan4.getUTCDay() + 6) % 7; // Monday = 0
  return new Date(jan4.getTime() - offset * DAY_MS + (w - 1) * 7 * DAY_MS);
}

/** The ISO week a UTC date falls in (the Thursday rule). */
function isoWeekOf(date: Date): { y: number; w: number } {
  const offset = (date.getUTCDay() + 6) % 7;
  const thursday = new Date(date.getTime() + (3 - offset) * DAY_MS);
  const y = thursday.getUTCFullYear();
  const dayOfYear = Math.floor((thursday.getTime() - Date.UTC(y, 0, 1)) / DAY_MS);
  return { y, w: Math.floor(dayOfYear / 7) + 1 };
}

/** 52 or 53: the ISO week Dec 28 falls in is always the year's last. */
export function weeksInIsoYear(y: number): number {
  return isoWeekOf(new Date(Date.UTC(y, 11, 28))).w;
}

function mondayOf(period: string): Date | null {
  const parts = periodParts(period);
  return parts ? isoWeekMonday(parts.y, parts.w) : null;
}

function shortDate(d: Date, withYear: boolean): string {
  const base = `${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}`;
  return withYear ? `${base}, ${d.getUTCFullYear()}` : base;
}

/**
 * `'2026-W37'` → `'Week 37, 2026 · Sep 7–13'`; a week that crosses a month or
 * a year spells both ends (`'Week 53, 2026 · Dec 28, 2026 – Jan 3, 2027'`).
 * Anything else comes back unchanged.
 */
export function periodLabel(period: string): string {
  const parts = periodParts(period);
  const monday = mondayOf(period);
  if (!parts || !monday) return period;
  const sunday = new Date(monday.getTime() + 6 * DAY_MS);
  let range: string;
  if (monday.getUTCFullYear() !== sunday.getUTCFullYear()) {
    range = `${shortDate(monday, true)} – ${shortDate(sunday, true)}`;
  } else if (monday.getUTCMonth() === sunday.getUTCMonth()) {
    range = `${shortDate(monday, false)}–${sunday.getUTCDate()}`;
  } else {
    range = `${shortDate(monday, false)} – ${shortDate(sunday, false)}`;
  }
  return `Week ${parts.w}, ${parts.y} · ${range}`;
}

/** Shift a period by whole weeks (negative allowed), across year ends. */
export function shiftPeriod(period: string, weeks: number): string {
  const monday = mondayOf(period);
  if (!monday) return period;
  const { y, w } = isoWeekOf(new Date(monday.getTime() + weeks * 7 * DAY_MS));
  return periodFromParts(y, w);
}

/** The ISO week today falls in, in New York. */
export function currentPeriod(today: { y: number; m: number; d: number } = nyToday()): string {
  const { y, w } = isoWeekOf(new Date(Date.UTC(today.y, today.m - 1, today.d)));
  return periodFromParts(y, w);
}

/** Newest first, `count` weeks back from and including the current one. */
export function periodOptions(
  today: { y: number; m: number; d: number } = nyToday(),
  count = 52,
): Array<{ id: string; label: string }> {
  const current = currentPeriod(today);
  return Array.from({ length: count }, (_, offset) => {
    const id = shiftPeriod(current, -offset);
    return { id, label: periodLabel(id) };
  });
}


export const INVOICE_STATES = [
  'pending_review', 'approved', 'xero_created', 'pdf_stored', 'emailed',
  'sent', 'paid', 'voided', 'skipped', 'failed',
] as const satisfies readonly InvoiceState[];

export const INVOICE_STATUS_FILTERS = ['all', ...INVOICE_STATES] as const;
export type InvoiceStatusFilter = (typeof INVOICE_STATUS_FILTERS)[number];

const statusFilter = searchEnum('status', INVOICE_STATUS_FILTERS, 'all');

/** Invoice ids are UUIDs; anything else in `?invoice=` is dropped. */
const SAFE_ID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
// The server returns every invoice in the range (each week's own plus its
// adjustments) and refuses a range holding more than the API's
// `MAX_LIST_ROWS` (240), so the span stays well under that.
const MAX_SPAN_WEEKS = 104;
const DEFAULT_SPAN_WEEKS = 26;

export interface InvoicesSearch {
  from: string;
  to: string;
  status: InvoiceStatusFilter;
  invoice?: string;
}

export function defaultInvoicesRange(today: { y: number; m: number; d: number } = nyToday()): {
  from: string;
  to: string;
} {
  const to = currentPeriod(today);
  return { from: shiftPeriod(to, -(DEFAULT_SPAN_WEEKS - 1)), to };
}

function weeksBetween(from: string, to: string): number {
  const a = mondayOf(from);
  const b = mondayOf(to);
  if (!a || !b) return Number.NaN;
  return Math.round((b.getTime() - a.getTime()) / (7 * DAY_MS));
}

/**
 * Total: a missing, malformed, inverted or over-long range falls back to the
 * default window; an unknown status becomes `all`; a bad id is dropped so a
 * typed URL cannot open a slot the screen has no branch for.
 */
export function parseInvoicesSearch(
  search: Record<string, unknown>,
  today: { y: number; m: number; d: number } = nyToday(),
): InvoicesSearch {
  const fallback = defaultInvoicesRange(today);
  let from = isPeriod(search.from) ? search.from : fallback.from;
  let to = isPeriod(search.to) ? search.to : fallback.to;
  const span = weeksBetween(from, to);
  if (!(span >= 0 && span < MAX_SPAN_WEEKS)) {
    from = fallback.from;
    to = fallback.to;
  }
  const out: InvoicesSearch = { from, to, status: statusFilter.parse(search) };
  if (typeof search.invoice === 'string' && SAFE_ID.test(search.invoice)) {
    out.invoice = search.invoice;
  }
  return out;
}

export function updateInvoicesSearch(
  current: InvoicesSearch,
  patch: Partial<InvoicesSearch>,
): InvoicesSearch {
  const merged: Record<string, unknown> = { ...current, ...patch };
  // `invoice: undefined` in a patch means "close the drawer", not "keep it".
  if ('invoice' in patch && patch.invoice === undefined) delete merged.invoice;
  return parseInvoicesSearch(merged);
}

/** The list read's query object. `status: 'all'` omits the key entirely. */
export function invoicesListQuery(s: Pick<InvoicesSearch, 'from' | 'to' | 'status'>): InvoicesQuery {
  const q: InvoicesQuery = { period_from: s.from, period_to: s.to };
  if (s.status !== 'all') q.state = s.status;
  return q;
}

// ── Vocabulary → bands ───────────────────────────────────────────────────────

export const STATE_LABEL: Record<InvoiceState, string> = {
  pending_review: 'PENDING REVIEW',
  approved: 'APPROVED',
  xero_created: 'IN XERO',
  pdf_stored: 'PDF ARCHIVED',
  emailed: 'EMAILED',
  sent: 'SENT',
  paid: 'PAID',
  voided: 'VOIDED',
  skipped: 'NO SPEND',
  failed: 'FAILED',
};

export function stateBand(state: InvoiceState): Band {
  switch (state) {
    case 'pending_review':
    case 'approved':
    case 'xero_created':
    case 'pdf_stored':
      return 'warn';
    case 'failed':
      return 'critical';
    case 'emailed':
    case 'sent':
    case 'paid':
      return 'ok';
    default:
      return 'unknown';
  }
}

export function xeroStatusLabel(status: string | null | undefined): string {
  return status ? status : 'not in Xero';
}

export function xeroStatusBand(status: string | null | undefined): Band {
  switch (status) {
    case 'PAID':
      return 'ok';
    case 'VOIDED':
    case 'DELETED':
      return 'critical';
    case 'AUTHORISED':
    case 'SUBMITTED':
      return 'warn';
    default:
      return 'unknown';
  }
}

export const EMAIL_STATUS_LABEL: Record<InvoiceEmailStatus, string> = {
  not_sent: 'Not sent',
  delivered: 'Delivered',
  queued: 'Sent · unverified',
  bounced: 'Bounced',
  failed: 'Failed',
  unknown: 'Unknown',
};

export function emailStatusBand(status: InvoiceEmailStatus): Band {
  switch (status) {
    case 'delivered':
      return 'ok';
    case 'queued':
      return 'warn';
    case 'bounced':
    case 'failed':
    case 'unknown':
      return 'critical';
    default:
      return 'unknown';
  }
}

export const ACTION_KIND_LABEL: Record<InvoiceActionKind, string> = {
  generate_period: 'Generate period',
  approve_send: 'Approve & send',
  resend_email: 'Resend email',
  sync_xero: 'Sync from Xero',
  mark_voided: 'Mark voided',
  raise_adjustment: 'Raise adjustment',
};

export const ACTION_STATUS_LABEL: Record<InvoiceAction['status'], string> = {
  queued: 'Waiting',
  running: 'In progress',
  done: 'Done',
  failed: 'Failed',
};

/**
 * A failure that did no harm yet reads as a warning, not an error: the email went out and
 * only Xero's record of it, or the delivery confirmation, is missing.
 */
export function failureTone(code: string | null | undefined): 'bad' | 'warn' {
  return code === 'invoice_sent_flag_pending' || (code?.startsWith('delivery_') ?? false) ? 'warn' : 'bad';
}

/** A failed action a later step made moot reads as history, not as a problem. */
export function actionStatusLabel(action: Pick<InvoiceAction, 'status' | 'resolved' | 'superseded'>): string {
  if (action.status === 'failed' && action.resolved) return 'Failed, then fixed';
  if (action.status === 'failed' && action.superseded) return 'Replaced by a new version';
  return ACTION_STATUS_LABEL[action.status];
}

export function actionStatusBand(action: Pick<InvoiceAction, 'status' | 'resolved' | 'superseded'>): Band {
  switch (action.status) {
    case 'done':
      return 'ok';
    case 'queued':
    case 'running':
      return 'warn';
    case 'failed':
      return action.resolved || action.superseded ? 'unknown' : 'critical';
  }
}

/**
 * What an action was, in words. An `approve_send` asked for after the invoice was emailed
 * is Finish in Xero (it runs only the sent flag), whatever the kind on the wire says.
 */
export function actionKindLabel(
  action: Pick<InvoiceAction, 'kind' | 'created_at'>,
  invoice: Pick<InvoiceSummary, 'emailed_at'>,
): string {
  if (action.kind === 'approve_send' && invoice.emailed_at && action.created_at > invoice.emailed_at) {
    return 'Finish in Xero';
  }
  return ACTION_KIND_LABEL[action.kind];
}

/** Who asked: a person's name, or SAM for what the scan or auto-approve queued. */
export function actorLabel(name: string | null | undefined): string {
  return name?.trim() ? name.trim() : 'SAM';
}

/** What an approve_send does from where the invoice is now. */
export function approveSendLabel(invoice: Pick<InvoiceSummary, 'state'>): string {
  switch (invoice.state) {
    case 'emailed':
      return 'Finish in Xero';
    case 'failed':
      return 'Retry';
    default:
      return 'Approve & send';
  }
}

/** The confirm button inside the approve panel. */
export function approveSendConfirmLabel(invoice: Pick<InvoiceSummary, 'state'>): string {
  switch (invoice.state) {
    case 'emailed':
      return 'Finish';
    case 'failed':
      return 'Retry';
    default:
      return 'Send';
  }
}

/**
 * What a Retry (or Finish) will do, from the step it resumes at — never "create and email"
 * for an invoice the client already has. Mirrors the executor's resume
 * (`failed_from_state`, `approved` when unknown) and `advance` from `emailed`.
 */
export function approveSendEffect(
  invoice: Pick<InvoiceSummary, 'state' | 'adjustment_seq'> & Partial<Pick<InvoiceSummary, 'failed_from_state'>>,
): string {
  const what = isAdjustment(invoice) ? 'adjustment invoice' : 'invoice';
  const from = invoice.state === 'failed' ? invoice.failed_from_state ?? 'approved' : invoice.state;
  switch (from) {
    case 'emailed':
      return `mark the ${what} as sent in Xero. The email has already been sent; nothing is emailed again`;
    case 'xero_created':
    case 'pdf_stored':
      return `finish storing the ${what}'s PDF and email it to the configured recipients. It is already in Xero`;
    default:
      return `create this ${what} in Xero and email the PDF to the configured recipients`;
  }
}

/**
 * An approval types the period (or an adjustment's reference) because it reaches the
 * client. Finish in Xero only updates Xero's record of a send that already happened.
 */
export function approveNeedsTyping(invoice: Pick<InvoiceSummary, 'state'>): boolean {
  return invoice.state !== 'emailed';
}

/** Which kind of send an attempt was. */
export function emailAttemptTitle(attempt: Pick<InvoiceEmailAttempt, 'kind' | 'attempt'>): string {
  return attempt.kind === 'resend' ? `Resent email (attempt ${attempt.attempt})` : 'Original email';
}

/**
 * One recipient's result on one attempt, in words, and its band. Red is kept for evidence
 * that delivery failed; an address the provider has said nothing about is "Not confirmed",
 * grey, whatever the attempt's own status.
 */
export function recipientResult(
  attempt: Pick<InvoiceEmailAttempt, 'status' | 'failed' | 'permanent_bounces' | 'suppressed' | 'queued' | 'delivered'>,
  address: string,
): { label: string; band: Band } {
  if (attempt.failed?.includes(address)) return { label: 'Delivery failed', band: 'critical' };
  if (attempt.permanent_bounces.includes(address)) return { label: 'Bounced', band: 'critical' };
  if (attempt.suppressed.includes(address)) return { label: 'Blocked by the email service', band: 'critical' };
  if (attempt.delivered.includes(address)) return { label: 'Delivered', band: 'ok' };
  if (attempt.queued.includes(address)) return { label: 'Waiting for the mail server', band: 'warn' };
  switch (attempt.status) {
    case 'failed':
      return { label: 'Not sent', band: 'critical' };
    case 'sending':
    case 'unknown':
      return { label: 'May not have been sent', band: 'warn' };
    default:
      return { label: 'Not confirmed', band: 'unknown' };
  }
}

// ── Rows ─────────────────────────────────────────────────────────────────────

export interface InvoiceRow extends InvoiceSummary {
  periodLabel: string;
  attention: 'review' | 'failed' | null;
  /** An adjustment listed directly under its week's own invoice. */
  nested: boolean;
}

/** Newest week first; inside a week its own invoice, then its adjustments in order. */
export function toInvoiceRows(list: readonly InvoiceSummary[]): InvoiceRow[] {
  const ids = new Set(list.map((invoice) => invoice.id));
  return [...list]
    .sort((a, b) =>
      a.period === b.period
        ? (a.adjustment_seq - b.adjustment_seq) || b.updated_at.localeCompare(a.updated_at)
        : b.period.localeCompare(a.period),
    )
    .map((invoice) => ({
      ...invoice,
      periodLabel: periodLabel(invoice.period),
      attention:
        invoice.state === 'pending_review' ? 'review' : invoice.state === 'failed' ? 'failed' : null,
      nested: isAdjustment(invoice) && invoice.adjusts_invoice_id != null && ids.has(invoice.adjusts_invoice_id),
    }));
}

// ── Adjustments ──────────────────────────────────────────────────────────────
//
// A sent week whose settled delivery grows gets an adjustment invoice
// (`SMART-YYYY-Www-ADJn`, the next SHUBERT- number) billing only the
// difference. The week's own invoice carries `adjustment_status`, derived
// server-side; an adjustment carries `adjusts_*`, naming the week's invoice.

type AdjustmentSettings = Pick<InvoicingSettings, 'adjustments_enabled' | 'adjustment_min_cents'>;

export function isAdjustment(invoice: Pick<InvoiceSummary, 'adjustment_seq'>): boolean {
  return invoice.adjustment_seq > 0;
}

/** `'Adjustment 1 to SHUBERT-0008'`, or `null` on a week's own invoice. */
export function adjustmentChip(
  invoice: Pick<InvoiceSummary, 'adjustment_seq' | 'adjusts_invoice_number' | 'adjusts_reference'>,
): string | null {
  if (!isAdjustment(invoice)) return null;
  const original = invoice.adjusts_invoice_number ?? invoice.adjusts_reference ?? 'the week’s invoice';
  return `Adjustment ${invoice.adjustment_seq} to ${original}`;
}

/** Mirrors `shared::invoicing::adjustment_is_open`: not yet sent, voided or withdrawn. */
export function adjustmentIsOpen(state: InvoiceState): boolean {
  return state !== 'sent' && state !== 'paid' && state !== 'voided' && state !== 'skipped';
}

/** The short note under a week's status pill, or `null` when there is nothing to say. */
export function adjustmentListNote(invoice: Pick<InvoiceSummary, 'adjustment_status'>): string | null {
  switch (invoice.adjustment_status) {
    case 'adjustment_open':
      return 'Adjustment open';
    case 'credit_needed':
      return 'Credit needed';
    case 'accumulating':
      return 'Accumulating';
    case 'awaiting_settlement':
      return 'Not yet billed';
    default:
      return null;
  }
}

export interface AdjustmentBanner {
  tone: 'info' | 'warn' | 'bad';
  headline: string;
  detail: string;
}

/**
 * What the drawer says about a week against its settled basis. On the week's
 * own invoice it names one of: the open adjustment, a credit to issue by hand,
 * growth accumulating below the minimum, growth waiting for settled data, or
 * the adjustments already sent. On an adjustment it names the week's invoice.
 */
export function adjustmentBanner(
  invoice: InvoiceSummary,
  adjustments: readonly InvoiceSummary[],
  settings: AdjustmentSettings | undefined,
): AdjustmentBanner | null {
  const number = invoice.xero_invoice_number ?? invoice.reference;
  if (isAdjustment(invoice)) {
    const original = invoice.adjusts_invoice_number ?? invoice.adjusts_reference ?? 'the week’s invoice';
    const originalRef = invoice.adjusts_invoice_number && invoice.adjusts_reference ? ` (${invoice.adjusts_reference})` : '';
    if (isParkedBesideDeadWeek(invoice)) {
      const dead = invoice.adjusts_state ?? 'voided';
      if (!invoice.xero_invoice_id) {
        return {
          tone: 'bad',
          headline: `Stopped: ${original} is no longer payable`,
          detail:
            `${invoice.reference} was not created in Xero now, but an earlier attempt to create it may have reached Xero. ` +
            `Look it up in Xero by ${invoice.reference} and void it there if present. Retry asks Xero again: it records ` +
            `what Xero holds, or withdraws the adjustment once nothing is found ${LOST_CREATE_VISIBILITY_MINUTES} minutes ` +
            `after the last attempt. Nothing is created or emailed while ${original} is ${dead}.`,
        };
      }
      if (invoice.email_status === 'unknown') {
        return {
          tone: 'bad',
          headline: `Stopped: ${original} is no longer payable`,
          detail:
            `${number} is in Xero, and an earlier email of it has no recorded outcome, so it may have been delivered: ` +
            `check the mailbox. Its email says ${original} remains payable as issued. Void ${number} in Xero, then ` +
            `Sync from Xero, or settle it with the client by hand. Retry and Resend are refused while ${original} is ${dead}.`,
        };
      }
      return {
        tone: 'bad',
        headline: `Stopped: ${original} is no longer payable`,
        detail:
          `${number} is in Xero but was not emailed: its email would say ${original} remains payable as issued. ` +
          `Void ${number} in Xero, then Sync from Xero, or settle it with the client by hand. Retry is refused while ${original} is ${dead}.`,
      };
    }
    if (besideDeadWeek(invoice)) {
      return {
        tone: 'bad',
        headline: `${original} is ${invoice.adjusts_state}`,
        detail:
          `${invoice.reference} is not approved, retried or resent while ${original} is ${invoice.adjusts_state}: ` +
          `its email says ${original} remains payable as issued.`,
      };
    }
    return {
      tone: 'info',
      headline: `${adjustmentChip(invoice)}${originalRef}`,
      detail:
        `It bills only delivery our platforms reported for ${periodLabel(invoice.period)} after ${original} was issued; ` +
        `${original} is unchanged. Each line shows what was invoiced before and the settled final.`,
    };
  }
  const drift = invoice.basis_drift_total_cents ?? 0;
  switch (invoice.adjustment_status) {
    case 'adjustment_open': {
      const open = adjustments.find((adjustment) => adjustmentIsOpen(adjustment.state));
      const label = open ? open.reference : 'An adjustment';
      const where = open
        ? open.state === 'pending_review'
          ? 'pending review'
          : `on its way to Xero (${STATE_LABEL[open.state].toLowerCase()})`
        : 'open';
      const refusedCreate = open != null && adjustmentNeverReachedXero(open);
      return {
        tone: refusedCreate ? 'bad' : 'warn',
        headline: `Adjustment ${label} ${where}`,
        detail:
          (open ? `It bills ${formatCents(open.total_cents, true)} delivered after ${number} was sent. ` : '') +
          (refusedCreate
            ? 'Xero never created it: Retry resends the same body, Re-freeze rebuilds it from the current config and returns it to review. '
            : '') +
          'Any later growth waits until it is sent.',
      };
    }
    case 'credit_needed':
      return {
        tone: 'bad',
        headline: `Billed more than delivered: ${formatCents(-(invoice.basis_credit_cents ?? 0))}`,
        detail: `Issue a credit note in Xero for ${number}. A decrease is never invoiced automatically.`,
      };
    case 'accumulating':
      return {
        tone: 'info',
        headline: `Accumulating: ${formatCents(drift, true)} (below the ${formatCents(settings?.adjustment_min_cents ?? 2500)} minimum)`,
        detail: 'Delivery reported after this invoice was sent is not billed until it reaches the minimum.',
      };
    case 'awaiting_settlement':
      return {
        tone: 'warn',
        headline: `Waiting for settled data: ${formatCents(drift, true)} not billed yet`,
        detail: settings && !settings.adjustments_enabled
          ? 'Adjustments are off in Admin → Config → Invoicing → Adjustments; review the difference in Xero.'
          : 'An adjustment is raised on the run weekday once no day of the week is incomplete, TTD has reported through the week and 3.5 days have passed since it closed.',
      };
    case 'settled': {
      const sent = adjustments.filter((adjustment) => adjustment.state === 'sent' || adjustment.state === 'paid');
      if (sent.length === 0) return null;
      const total = sent.reduce((sum, adjustment) => sum + adjustment.total_cents, invoice.total_cents);
      const parts = [invoice, ...sent]
        .map((part) => `${part.xero_invoice_number ?? part.reference} ${formatCents(part.total_cents)}`)
        .join(' + ');
      return {
        tone: 'info',
        headline: `${sent.length === 1 ? 'Adjustment' : 'Adjustments'} ${sent.map((a) => a.reference).join(', ')} sent`,
        detail: `Week total now ${formatCents(total)} (${parts}).`,
      };
    }
    default:
      return null;
  }
}

/** Raise adjustment now: a sent week whose settled growth is at or above the minimum. */
export function canRaiseAdjustment(
  invoice: Pick<InvoiceSummary, 'state' | 'adjustment_seq' | 'adjustment_status'>,
  settings: AdjustmentSettings | undefined,
  actions: readonly Pick<InvoiceAction, 'status'>[],
): boolean {
  return (
    !isAdjustment(invoice) &&
    (invoice.state === 'sent' || invoice.state === 'paid') &&
    settings?.adjustments_enabled === true &&
    invoice.adjustment_status === 'awaiting_settlement' &&
    !hasOpenAction(actions)
  );
}

/** A failed adjustment with nothing in Xero (a refused create). Mirrors `shared::invoicing::can_refreeze`. */
function adjustmentNeverReachedXero(adjustment: Pick<InvoiceSummary, 'state' | 'xero_invoice_id'>): boolean {
  return adjustment.state === 'failed' && !adjustment.xero_invoice_id;
}

/**
 * The week's open adjustment when Xero refused its create (failed, no Xero
 * id): `raise_adjustment` on the week re-freezes it from the current config
 * and returns it to review — the remedy Generate period gives a week's own
 * invoice. `null` when there is nothing to re-freeze.
 */
export function refreezableAdjustment(
  invoice: Pick<InvoiceSummary, 'state' | 'adjustment_seq'>,
  adjustments: readonly InvoiceSummary[],
  settings: AdjustmentSettings | undefined,
  actions: readonly Pick<InvoiceAction, 'status'>[],
): InvoiceSummary | null {
  if (isAdjustment(invoice) || (invoice.state !== 'sent' && invoice.state !== 'paid')) return null;
  if (settings?.adjustments_enabled !== true || hasOpenAction(actions)) return null;
  return adjustments.find(adjustmentNeverReachedXero) ?? null;
}

/** One adjustment line's before → final, for the drawer. */
export interface AdjustmentLineView {
  invoicedImpressions: number;
  finalImpressions: number;
  invoicedCents: number;
  finalCents: number;
}

export function adjustmentLineView(line: InvoiceLine): AdjustmentLineView | null {
  if (line.adjusts_spend_cents == null || line.final_spend_cents == null) return null;
  return {
    invoicedImpressions: line.adjusts_impressions ?? 0,
    finalImpressions: line.final_impressions ?? 0,
    invoicedCents: line.adjusts_spend_cents,
    finalCents: line.final_spend_cents,
  };
}

export function summarizeInvoices(rows: readonly InvoiceSummary[]): {
  total: number;
  pendingReview: number;
  failed: number;
  billedCents: number;
} {
  return {
    total: rows.length,
    pendingReview: rows.filter((r) => r.state === 'pending_review').length,
    failed: rows.filter((r) => r.state === 'failed').length,
    billedCents: rows
      .filter((r) => r.state !== 'skipped' && r.state !== 'voided')
      .reduce((sum, r) => sum + r.total_cents, 0),
  };
}

export function emptyInvoicesMessage(search: InvoicesSearch): string {
  const range = `${periodLabel(search.from)} – ${periodLabel(search.to)}`;
  return search.status === 'all'
    ? `No invoices between ${range}. The daily scan freezes a closed week on its run weekday; use Generate period to do it now.`
    : `No ${STATE_LABEL[search.status].toLowerCase()} invoices between ${range}.`;
}

// ── Actions ──────────────────────────────────────────────────────────────────

export function hasOpenAction(actions: readonly Pick<InvoiceAction, 'status'>[]): boolean {
  return actions.some((a) => a.status === 'queued' || a.status === 'running');
}

export function latestAction(actions: readonly InvoiceAction[]): InvoiceAction | null {
  return [...actions].sort((a, b) => b.created_at.localeCompare(a.created_at))[0] ?? null;
}

/** The queued or running action, if any: what a progress notice is about. */
export function openAction(actions: readonly InvoiceAction[]): InvoiceAction | null {
  return (
    [...actions]
      .filter((a) => a.status === 'queued' || a.status === 'running')
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0] ?? null
  );
}

type Gate = Pick<InvoiceSummary, 'state' | 'pdf_available' | 'xero_invoice_id' | 'last_error_kind'> &
  Partial<Pick<InvoiceSummary, 'adjustment_seq' | 'adjusts_state' | 'failed_from_state'>>;

/** Mirrors `shared::invoicing::ADJUSTMENT_LOST_CREATE_VISIBILITY_MS`, in minutes. */
export const LOST_CREATE_VISIBILITY_MINUTES = 15;

/** Mirrors `shared::invoicing::adjustment_original_payable`: only a sent or paid week carries an adjustment. */
export function adjustmentOriginalPayable(state: InvoiceState): boolean {
  return state === 'sent' || state === 'paid';
}

/**
 * An adjustment whose week's invoice no longer stands, as the projection
 * reports it (`adjusts_state`). Nothing carries it towards Xero or a mailbox:
 * its email says that invoice remains payable as issued.
 */
export function besideDeadWeek(invoice: Partial<Pick<InvoiceSummary, 'adjustment_seq' | 'adjusts_state'>>): boolean {
  return (invoice.adjustment_seq ?? 0) > 0 && invoice.adjusts_state != null && !adjustmentOriginalPayable(invoice.adjusts_state);
}

/**
 * Mirrors `shared::invoicing::adjustment_relookup_retry`: the one Retry allowed
 * beside a dead week, a create that never landed (failed from `approved`, no
 * Xero id). It only asks Xero again; it never creates.
 */
export function adjustmentRelookupRetry(
  invoice: Pick<InvoiceSummary, 'state' | 'xero_invoice_id'> & Partial<Pick<InvoiceSummary, 'failed_from_state'>>,
): boolean {
  return (
    invoice.state === 'failed' &&
    (invoice.failed_from_state == null || invoice.failed_from_state === 'approved') &&
    !invoice.xero_invoice_id?.trim()
  );
}

/**
 * An adjustment the ladder stopped because the week's invoice it supplements
 * was voided after approval (`adjustment_original_not_payable`): parked, never
 * emailed by the ladder. Retry is refused server-side unless the create never
 * landed ([`adjustmentRelookupRetry`]).
 */
export function isParkedBesideDeadWeek(invoice: Pick<InvoiceSummary, 'state' | 'last_error_kind'>): boolean {
  return invoice.state === 'failed' && invoice.last_error_kind === 'adjustment_original_not_payable';
}

export function canApproveSend(invoice: Gate, actions: readonly Pick<InvoiceAction, 'status'>[]): boolean {
  const dead = besideDeadWeek(invoice) || isParkedBesideDeadWeek(invoice);
  // From `emailed` it is Finish in Xero: only the sent flag is left (the API and the
  // executor admit it the same way, with no action open).
  const finish = invoice.state === 'emailed' && invoice.xero_invoice_id != null;
  return (
    (invoice.state === 'pending_review' || invoice.state === 'failed' || finish) &&
    (!dead || adjustmentRelookupRetry(invoice)) &&
    !hasOpenAction(actions)
  );
}

/**
 * Generate period may re-freeze a week's existing invoice while it is still in
 * review or skipped, or after a failure that left nothing in Xero (no invoice
 * id): a refused create is re-frozen from the current config and returns to
 * review. Mirrors `shared::invoicing::can_refreeze`.
 */
export function canRegeneratePeriod(invoice: Pick<InvoiceSummary, 'state' | 'xero_invoice_id'>): boolean {
  if (invoice.state === 'pending_review' || invoice.state === 'skipped') return true;
  return invoice.state === 'failed' && !invoice.xero_invoice_id;
}

export function canResendEmail(invoice: Gate, actions: readonly Pick<InvoiceAction, 'status'>[]): boolean {
  const allowedState =
    invoice.state === 'emailed' ||
    invoice.state === 'sent' ||
    invoice.state === 'paid' ||
    (invoice.state === 'failed' && invoice.last_error_kind === 'email_outcome_unknown');
  return allowedState && !besideDeadWeek(invoice) && invoice.pdf_available && !hasOpenAction(actions);
}

export function canSyncXero(invoice: Gate, actions: readonly Pick<InvoiceAction, 'status'>[]): boolean {
  return invoice.xero_invoice_id != null && !hasOpenAction(actions);
}

export function canOpenPdf(invoice: Gate): boolean {
  return invoice.pdf_available;
}

/**
 * The literal an admin must type to approve: the period itself, or for an
 * adjustment its reference (`SMART-2026-W39-ADJ1`) — a period alone names the
 * week's invoice, which was already sent.
 */
export function approveConfirmation(invoice: Pick<InvoiceSummary, 'period' | 'reference' | 'adjustment_seq'>): string {
  return isAdjustment(invoice) ? invoice.reference : invoice.period;
}

export function confirmationState(expected: string, typed: string): 'idle' | 'match' | 'mismatch' {
  if (typed === '') return 'idle';
  return typed === expected ? 'match' : 'mismatch';
}

export function canConfirmApprove(
  invoice: Gate & Pick<InvoiceSummary, 'period' | 'reference' | 'adjustment_seq'>,
  actions: readonly Pick<InvoiceAction, 'status'>[],
  typed: string,
  pending = false,
): boolean {
  return (
    !pending &&
    canApproveSend(invoice, actions) &&
    (!approveNeedsTyping(invoice) || confirmationState(approveConfirmation(invoice), typed) === 'match')
  );
}

// ── Money and drift ──────────────────────────────────────────────────────────

/** `$12,345.67`; negatives as `-$1.00`. Plain `toLocaleString` on a number. */
export function formatCents(cents: number, signed = false): string {
  const sign = cents < 0 ? '-' : signed && cents > 0 ? '+' : '';
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100).toLocaleString();
  const rest = String(abs % 100).padStart(2, '0');
  return `${sign}$${dollars}.${rest}`;
}

export function hasBasisDrift(
  invoice: Pick<InvoiceSummary, 'basis_drift_detected_at' | 'basis_drift_total_cents'>,
): boolean {
  return invoice.basis_drift_detected_at != null ||
    (invoice.basis_drift_total_cents != null && invoice.basis_drift_total_cents !== 0);
}

export function driftWarning(
  invoice: Pick<InvoiceSummary, 'period' | 'basis_drift_total_cents' | 'basis_drift_detected_at' | 'state'>,
): { headline: string; detail: string } | null {
  const drift = invoice.basis_drift_total_cents;
  if (!hasBasisDrift(invoice)) return null;
  const label = periodLabel(invoice.period);
  return {
    headline: `BASIS DRIFT — delivery for ${label} restated after this invoice was frozen`,
    detail:
      (drift === 0
        ? 'Individual campaign charges changed, while the overall total is unchanged. '
        : drift == null
          ? 'The invoice basis changed; a total difference is not available. '
          : `Billable gross is now ${formatCents(drift, true)} versus the frozen total. `) +
      (invoice.state === 'pending_review'
        ? 'The lines will be re-frozen on the next scan or when you approve.'
        : drift === 0
          ? 'The issued invoice has not been changed.'
          : 'The invoice already left review; review the difference in Xero.'),
  };
}

export function reportingUpdate(
  invoice: Pick<InvoiceSummary, 'basis_reporting_changed_at' | 'basis_reporting_impressions_delta'>,
): string | null {
  if (!invoice.basis_reporting_changed_at) return null;
  const delta = invoice.basis_reporting_impressions_delta;
  const change = delta != null && delta !== 0
    ? ` (${delta > 0 ? '+' : ''}${delta.toLocaleString()} impression${Math.abs(delta) === 1 ? '' : 's'})`
    : '';
  return `Reporting updated${change}. Every campaign charge and the invoice total are unchanged. No billing adjustment is needed.`;
}

// ── Lines ────────────────────────────────────────────────────────────────────

export interface LineGroup {
  productionId: string;
  productionName: string;
  /** Media + fee: the group's gross. */
  subtotalCents: number;
  mediaCents: number;
  feeCents: number;
  lines: InvoiceLine[];
}

/** The media line: the campaign's gross less the frozen management fee. */
export function lineMediaCents(line: Pick<InvoiceLine, 'spend_cents' | 'fee_cents'>): number {
  return line.spend_cents - line.fee_cents;
}

export function groupLinesByProduction(lines: readonly InvoiceLine[]): LineGroup[] {
  const groups = new Map<string, LineGroup>();
  for (const line of [...lines].sort((a, b) => a.line_no - b.line_no)) {
    const group = groups.get(line.production_id) ?? {
      productionId: line.production_id,
      productionName: line.production_name,
      subtotalCents: 0,
      mediaCents: 0,
      feeCents: 0,
      lines: [],
    };
    group.lines.push(line);
    group.subtotalCents += line.spend_cents;
    group.mediaCents += lineMediaCents(line);
    group.feeCents += line.fee_cents;
    groups.set(line.production_id, group);
  }
  return [...groups.values()].sort((a, b) => a.productionName.localeCompare(b.productionName));
}

export function linesTotal(lines: readonly InvoiceLine[]): number {
  return lines.reduce((sum, line) => sum + line.spend_cents, 0);
}

export function linesFeeTotal(lines: readonly InvoiceLine[]): number {
  return lines.reduce((sum, line) => sum + line.fee_cents, 0);
}

/** `2500` → `25%`, `1250` → `12.5%`, `25` → `0.25%`. */
export function feeRateLabel(bps: number): string {
  const whole = Math.floor(bps / 100);
  const hundredths = bps % 100;
  if (hundredths === 0) return `${whole}%`;
  return `${whole}.${String(hundredths).padStart(2, '0').replace(/0+$/, '')}%`;
}

export function channelLabel(channel: string): string {
  switch (channel) {
    case 'meta':
      return 'Meta';
    case 'ctv':
      return 'CTV';
    case 'dsp':
      return 'Display';
    default:
      return channel;
  }
}

/** The send response is not a later delivery receipt. Never claim a mailbox was read. */
export function emailDeliveryNote(kind?: string | null): string {
  switch (kind) {
    case 'provider_auth':
    case 'provider_auth_header':
    case 'provider_auth_http_401':
    case 'provider_auth_http_403':
    case 'provider_auth_graphql':
      return 'Delivery verification needs Zone Analytics Read permission on the sending domain.';
    case 'provider_zone_not_visible': return 'Delivery verification cannot see the sending domain: check the zone in Admin › Config › Email and its Analytics Read permission.';
    case 'provider_zone_ambiguous': return 'Delivery verification got reports for more than one domain. Delivery remains unverified.';
    case 'history_expired': return 'Delivery records are outside the provider’s 31-day history. Delivery remains unverified.';
    case 'tracking_not_configured': return 'Automatic delivery verification is not configured.';
    case 'delivery_unverified': return 'The provider has not confirmed delivery for every recipient. Verification will retry automatically.';
    case 'status_unrecognised': return 'The provider reported a delivery status this build does not recognise; the word is in the SAM Server log. Delivery remains unverified.';
    case null: case undefined: return '';
    default: return 'Delivery verification is temporarily unavailable. It will retry automatically; the email will not be resent.';
  }
}
